import React, { ReactElement, useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { RiCloseLine } from "react-icons/ri";

import useEvents from "../../contexts/PortalSubscriptionContext";
import useSelectionState from "../../pages/agent/selection-state";
import { ErrorView } from "../../pages/error/Error";
import {
    PortalEventType, PortalParticipantEvent, useGetConversationUsersQuery,
    useGetParticipantsForChatQuery, User, useRemoveParticipantMutation,
} from "../../__generated__/api";
import Avatar from "../avatar/Avatar";
import Confirm from "../confirm/Confirm";
import { LoadingIcon } from "../loading/Loading";
import UserOverlay from "../user-overlay/UserOverlay";

function UserOverlayWrapper({
    user, onToggleShow, shouldShow, onRemove,
}: {
    user: User,
    onToggleShow: (shouldShow: boolean) => void,
    shouldShow: boolean,
    onRemove?: (userId: User) => void
}): ReactElement {
    return (
        <UserOverlay
            shouldShow={shouldShow}
            onToggleShow={onToggleShow}
            user={user}
        >
            <div
                className="list-group-item list-group-item-action p-1 border-0 d-flex justify-content-between align-items-center"
                key={user.id}
            >
                <button
                    type="button"
                    className="btn btn-none p-0 flex-grow-1 text-start"
                    onClick={() => onToggleShow(!shouldShow)}
                >
                    <Avatar src={user.avatarUrl as string} variant="xs" />
                    {" "}
                    {user.name}
                </button>
                {onRemove != null && (
                    <button type="button" className="btn btn-none p-0" onClick={() => onRemove(user)}>
                        <RiCloseLine className="text-light" />
                    </button>
                )}

            </div>
        </UserOverlay>
    );
}
UserOverlayWrapper.defaultProps = {
    onRemove: undefined,
};

interface Props {
    chatId: string;
    conversationSourceId?: string;
}

export default function TicketPeople({ chatId, conversationSourceId }: Props): ReactElement {
    const { data: usersData, loading: isLoading, error } = useGetConversationUsersQuery({
        variables: {
            id: conversationSourceId,
        },
        skip: conversationSourceId == null,
    });

    const [removeParticipant] = useRemoveParticipantMutation();

    const {
        data: participantsData,
        loading: isParticipantsLoading,
        error: participantsError,
        refetch,
    } = useGetParticipantsForChatQuery({
        variables: {
            chatId,
        },
        skip: chatId == null,
    });
    const chatRefresh = (evt: {chatId: string}) => {
        if (evt.chatId === chatId) {
            refetch();
        }
    };

    useEvents(api => {
        api.on(PortalEventType.ParticipantsAdded, 0, (evt: PortalParticipantEvent) => chatRefresh(evt));
        api.on(PortalEventType.ParticipantsRemoved, 0, (evt: PortalParticipantEvent) => chatRefresh(evt));
    });
    const selectionState = useSelectionState();

    const [search, setSearch] = useState("");
    const [visibleCard, setVisibleCard] = useState<string | undefined>();

    const [deleteUser, setDeleteUser] = useState<User>();
    const [deleteUserNote, setDeleteUserNote] = useState("");

    const classNames = ["list-group-item text-gray-800"];
    classNames.push("list-border list-border-primary");

    const allUsers = usersData?.conversationSourceUsers;
    let users = allUsers;
    if (search) {
        const searchLower = search.toLowerCase();
        users = users?.filter(row => row.firstName?.toLowerCase().includes(searchLower)
            || row.lastName?.toLowerCase().includes(searchLower)
            || row.group?.toLowerCase().includes(searchLower));
    }

    const addParticipant = () => {
        selectionState.addParticipants(chatId);
    };

    useEffect(() => {
        if (deleteUser) {
            setDeleteUserNote("");
        }
    }, [deleteUser]);

    const onRemoveParticipant = async (userId: string) => {
        await removeParticipant({
            variables: {
                participant: {
                    chatIds: [chatId],
                    participantId: userId,
                    note: deleteUserNote,
                },
            },
        });
        setDeleteUser(undefined);
    };
    return (
        <div>
            <Confirm
                confirmText="Remove Participant"
                onConfirm={() => onRemoveParticipant(deleteUser?.id)}
                shouldShow={deleteUser != null}
                onHide={() => setDeleteUser(undefined)}
            >
                <div>
                    <div>
                        Click &quot;Remove Participant&quot; to confirm that you would like to remove the following participant:
                    </div>
                    <div className="mb-4 mt-2 alert alert-danger p-2">
                        <Avatar src={deleteUser?.avatarUrl as string} variant="xs" />
                        {deleteUser?.name}
                    </div>
                    <div>
                        <Form.Label className="text-primary">
                            Comments
                        </Form.Label>
                        <Form.Control
                            as="textarea"
                            type="text"
                            required
                            placeholder="Removing this participant because..."
                            className="default-text-height p-2"
                            onChange={evt => setDeleteUserNote(evt.target.value)}
                        />
                        <Form.Text>
                            Any additional notes you want to add to why you are removing this participant?
                        </Form.Text>
                    </div>
                </div>
            </Confirm>
            <div className="h-100 d-flex flex-column">

                <div className="list-group list-group-flush ms-2 flex-grow-1">

                    <div className={`mb-3 px-3 mt-3 rounded ${classNames.join(" ")}`}>
                        {isParticipantsLoading && <LoadingIcon />}
                        {participantsData?.getParticipants?.isNotEmpty ? (
                            <div>
                                <h6 className="">
                                    Participants
                                </h6>

                                {participantsError && <ErrorView message="Error loading participants" />}
                                <div className="list-group list-group-flush mt-2">
                                    {participantsData.getParticipants?.map(user => (
                                        <UserOverlayWrapper
                                            user={user as User}
                                            key={user.id}
                                            shouldShow={visibleCard === `participant-${user.id}`}
                                            onToggleShow={shouldDisplay => {
                                                if (!shouldDisplay) {
                                                    setVisibleCard(undefined);
                                                } else {
                                                    setVisibleCard(`participant-${user.id}`);
                                                }
                                            }}
                                            onRemove={deletingUser => setDeleteUser(deletingUser)}
                                        />
                                    ))}
                                </div>
                                <button
                                    type="button"
                                    className="d-block mt-2 btn btn-link btn-sm"
                                    onClick={addParticipant}
                                >
                                    Add Participant
                                </button>
                            </div>
                        ) : (
                            <div>
                                <h6 className="">
                                    Participants
                                </h6>
                                <small className="text-light">
                                    There are no participants in this chat. If you would like to add a participant,
                                    click Add Participants below.
                                </small>
                                <button
                                    type="button"
                                    className="d-block mt-2 btn btn-primary btn-sm py-2"
                                    onClick={addParticipant}
                                >
                                    Add Participant
                                </button>
                            </div>
                        )}
                    </div>

                    {(isLoading || allUsers?.isNotEmpty) && (
                        <div className={`mb-3 px-3 mt-3 rounded ${classNames.join(" ")}`}>
                            {isLoading && <LoadingIcon />}
                            {allUsers?.isNotEmpty && (
                                <div>
                                    <h6 className="">
                                        Related people
                                    </h6>

                                    {error && <ErrorView message="Error loading users" />}
                                    {allUsers.length > 10 && (
                                        <input
                                            type="search"
                                            className="form-control"
                                            placeholder="Search..."
                                            value={search}
                                            onChange={event => setSearch(event.target.value)}
                                        />
                                    )}
                                    <div className="list-group list-group-flush mt-2">
                                        {users?.limit(20).map(user => (
                                            <UserOverlayWrapper
                                                user={user as User}
                                                key={user.id}
                                                shouldShow={visibleCard === user.id}
                                                onToggleShow={shouldDisplay => {
                                                    if (!shouldDisplay) {
                                                        setVisibleCard(undefined);
                                                    } else {
                                                        setVisibleCard(user.id);
                                                    }
                                                }}
                                            />
                                        ))}

                                    </div>
                                    {!search && allUsers.length > 20 && (
                                        <small className="text-light">
                                            Too many users to display. Use search to discover more users.
                                        </small>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                </div>

            </div>
        </div>
    );
}

TicketPeople.defaultProps = {
    conversationSourceId: undefined,
};
